import { c as _c } from "react/compiler-runtime";
import { buildSessionsScopeParam } from "@/lib/assistant-sessions";
import { DEFAULT_GITHUB_INSTANCE_SLUG } from "@/lib/named-org";
import { useQuery } from "react-query";
import { useApi } from "./useApi";
import { useDebouncedValue } from "./useDebouncedValue";
import { useOrgFromPathname } from "./useOrgFromPathname";
import { useConnectedGithubOrgsNonSuspense } from "./useConnectedGithubOrgs";
import type { CachedAssistantSession } from "./useAssistantSessionCache";
export interface SemanticSessionMatch extends CachedAssistantSession {
  similarity: number;
  snippet: string;
}
const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 300;
const STALE_TIME = 60 * 1000; // 1 minute

// Semantic search over embedded Codesmith session transcripts, powering
// the "by meaning" session matches in the command palette. Complements the
// client-side title-substring search in useAssistantSessionCache.
export function useSemanticSessionSearch(searchTerm) {
  const $ = _c(21);
  const api = useApi();
  const activeGithubOrg = useOrgFromPathname();
  const {
    connectedGithubOrgs
  } = useConnectedGithubOrgsNonSuspense();
  let t0;
  if ($[0] !== activeGithubOrg || $[1] !== connectedGithubOrgs?.installations) {
    t0 = connectedGithubOrgs?.installations.find(installation => installation?.account?.login === activeGithubOrg)?.codesmith_enabled ?? false;
    $[0] = activeGithubOrg;
    $[1] = connectedGithubOrgs?.installations;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const codesmithEnabled = t0;
  let t1;
  if ($[3] !== searchTerm) {
    t1 = searchTerm.trim();
    $[3] = searchTerm;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const trimmed = t1;
  const debouncedTerm = useDebouncedValue(trimmed, DEBOUNCE_MS);
  const searchable = !!activeGithubOrg && codesmithEnabled;
  const enabled = searchable && debouncedTerm.length >= MIN_QUERY_LENGTH;
  let t2;
  if ($[5] !== activeGithubOrg || $[6] !== debouncedTerm) {
    t2 = ["semanticSessionSearch", DEFAULT_GITHUB_INSTANCE_SLUG, activeGithubOrg, debouncedTerm];
    $[5] = activeGithubOrg;
    $[6] = debouncedTerm;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== activeGithubOrg || $[9] !== api || $[10] !== debouncedTerm) {
    t3 = async () => {
      const response = await api.get(`/assistant/sessions/semantic-search?q=${encodeURIComponent(debouncedTerm)}${buildSessionsScopeParam(null, {
        orgName: activeGithubOrg,
        instanceSlug: DEFAULT_GITHUB_INSTANCE_SLUG
      })}`);
      return (response.data.sessions || []) as SemanticSessionMatch[];
    };
    $[8] = activeGithubOrg;
    $[9] = api;
    $[10] = debouncedTerm;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== enabled) {
    t4 = {
      enabled,
      staleTime: STALE_TIME,
      refetchOnWindowFocus: false
    };
    $[12] = enabled;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const {
    data,
    isLoading
  } = useQuery(t2, t3, t4);
  const isDebouncing = searchable && trimmed.length >= MIN_QUERY_LENGTH && trimmed !== debouncedTerm;
  let t5;
  if ($[14] !== data || $[15] !== enabled || $[16] !== isDebouncing) {
    t5 = enabled && !isDebouncing ? data ?? [] : [];
    $[14] = data;
    $[15] = enabled;
    $[16] = isDebouncing;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const t6 = enabled && isLoading || isDebouncing;
  let t7;
  if ($[18] !== t5 || $[19] !== t6) {
    t7 = {
      sessions: t5,
      isLoading: t6
    };
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
}